import {
  Suspense,
  lazy,
  useCallback,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import {
  AppShell,
  CommandPalette,
  EmptyState,
  EnvironmentBadge,
  Spinner,
  ToastProvider,
  type Command,
} from '@csa/ui';
import type { SessionResponse } from '../auth/useSession.js';
import { appConfig } from '../lib/app-config.js';
import { initialsFor } from '../auth/msal.js';
import { visibleNavigation } from './navigation.js';
import { Overview } from '../pages/Overview.js';
import { UsersPage } from '../pages/admin/UsersPage.js';
import { RolesPage } from '../pages/admin/RolesPage.js';
import { ApiConsole } from '../pages/api/ApiConsole.js';
import { AuditPage } from '../pages/admin/AuditPage.js';
import { AccessReviewPage } from '../pages/admin/AccessReviewPage.js';
import { EvidencePage } from '../pages/admin/EvidencePage.js';
import { TargetsPage } from '../pages/admin/TargetsPage.js';
import { ApprovalRulesPage } from '../pages/admin/ApprovalRulesPage.js';
import { JobsPage } from '../pages/jobs/JobsPage.js';
import { ApprovalsPage } from '../pages/approvals/ApprovalsPage.js';
import { SettingsPage } from '../pages/SettingsPage.js';
import { TimeZoneProvider, useTimeZone } from '../lib/TimeZoneProvider.js';

/**
 * The SQL console is the one page loaded on demand.
 *
 * It pulls in the editor, the grid and the splitter -- the three largest
 * dependencies in the bundle -- and the approvers and auditors who are a
 * large part of the user base never open it. Everything else here is small
 * enough that lazy-loading it would cost a round trip to save nothing.
 */
const SqlConsole = lazy(async () => ({
  default: (await import('../pages/sql/SqlConsole.js')).SqlConsole,
}));

/** True for the one route that wants the whole viewport rather than a column. */
function isFullBleed(path: string): boolean {
  return path.startsWith('/sql');
}

/**
 * The authenticated frame.
 *
 * Entitlements arrive with the session, so navigation is built from what this
 * user actually holds. Until the RBAC layer lands the session carries no
 * permission list, and the map degrades to the always-visible entries rather
 * than guessing -- showing a link optimistically and letting the route 403 is
 * a worse experience than not showing it.
 */
export function ConsoleShell({
  session,
  currentPath,
  onSignOut,
}: {
  session: SessionResponse;
  currentPath: string;
  onSignOut: () => void;
}): ReactElement {
  /**
   * The time-zone provider wraps the frame rather than sitting inside it,
   * because the frame itself displays the resolved zone in the sidebar. A
   * provider below that point would mean the label was computed by one rule
   * and the timestamps by another.
   */
  return (
    <ToastProvider>
      <TimeZoneProvider preference={session.preferences.timeZone}>
        <ConsoleFrame session={session} currentPath={currentPath} onSignOut={onSignOut} />
      </TimeZoneProvider>
    </ToastProvider>
  );
}

function ConsoleFrame({
  session,
  currentPath,
  onSignOut,
}: {
  session: SessionResponse;
  currentPath: string;
  onSignOut: () => void;
}): ReactElement {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const config = appConfig();
  const { timeZone, isFollowingBrowser } = useTimeZone();

  const held = useMemo(() => new Set<string>(session.entitlements.all), [session.entitlements.all]);
  const groups = useMemo(
    // The bootstrap administrator holds no stored entitlements by definition,
    // so the map would be empty and they could not reach the screen that
    // assigns the first role. The two administration permissions the server
    // honours for them are added here to match.
    () =>
      visibleNavigation(
        session.isBootstrapAdmin ? new Set([...held, 'role.manage', 'user.manage']) : held,
      ),
    [held, session.isBootstrapAdmin],
  );

  const navigate = useCallback((href: string) => {
    // pushState plus a popstate so the location hook in App picks it up. The
    // router replaces this wholesale when the consoles land.
    window.history.pushState({}, '', href);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  const commands = useMemo<Command[]>(
    () => [
      ...groups.flatMap((group) =>
        group.items.map((item) => ({
          id: `nav:${item.key}`,
          label: item.label,
          group: group.label,
          icon: item.icon,
          run: () => navigate(item.href),
        })),
      ),
      {
        id: 'session:signout',
        label: 'Sign out',
        group: 'Session',
        hint: 'Ends your Microsoft session too',
        run: onSignOut,
      },
    ],
    [groups, navigate, onSignOut],
  );

  return (
    <>
      <AppShell
        groups={groups}
        currentPath={currentPath}
        user={{
          displayName: session.user.displayName,
          upn: session.user.upn,
          initials: initialsFor({ name: session.user.displayName, username: session.user.upn }),
        }}
        onSignOut={onSignOut}
        onOpenCommandPalette={() => setPaletteOpen(true)}
        /**
         * The SQL console manages its own scrolling across three panes, so
         * it needs the viewport rather than a scrolling 100rem column -- a
         * splitter inside a column whose height is its content has nothing
         * to divide. Every other page is stacked cards and wants the column.
         */
        variant={isFullBleed(currentPath) ? 'app' : 'page'}
        buildSha={config.build.sha}
        buildTime={config.build.time}
        /**
         * The zone in use, always on screen.
         *
         * Times are rendered in the reader's zone -- detected from the
         * browser, overridable in settings -- which on its own would make an
         * audit trail whose times depend on who is reading it. Three things
         * stop that: this label, the zone appended to every rendered
         * timestamp, and exports that are UTC regardless of preference. A
         * reader can always tell what they are looking at, and two readers
         * comparing exports see identical bytes.
         */
        timezoneLabel={isFollowingBrowser ? `${timeZone} · detected` : timeZone}
        headerSlot={config.env === 'prod' ? null : <EnvironmentBadge environment={config.env} />}
        renderLink={({ href, className, children, ...rest }) => (
          <a
            href={href}
            className={className}
            {...rest}
            onClick={(event) => {
              // Let modified clicks open a new tab as the user expects.
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
              event.preventDefault();
              navigate(href);
            }}
          >
            {children}
          </a>
        )}
      >
        <Routes path={currentPath} session={session} />
      </AppShell>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} commands={commands} />
    </>
  );
}

/**
 * Placeholder routing. Replaced by the TanStack router once the consoles
 * exist; the shape is kept so the swap is local to this file.
 *
 * Reaching a route the user cannot use renders a refusal rather than the
 * page, but that is only tidiness -- the API refuses every underlying call
 * independently, so a hand-typed URL gains nothing.
 */
function Routes({ path, session }: { path: string; session: SessionResponse }): ReactNode {
  const held = new Set<string>(session.entitlements.all);
  const canAdminister =
    session.isBootstrapAdmin || held.has('role.manage') || held.has('user.manage');

  if (path.startsWith('/admin/users')) {
    return canAdminister ? <UsersPage /> : <NotPermitted />;
  }
  if (path.startsWith('/admin/roles')) {
    return canAdminister ? <RolesPage /> : <NotPermitted />;
  }

  /**
   * The consoles check for the permission generally, not against a
   * particular target -- target scoping is decided per request by the
   * server, and the console itself lists only the targets the user holds
   * something on. Someone with a grant on one database reaches the page and
   * sees exactly that one database in the picker.
   */
  if (path.startsWith('/sql')) {
    if (!held.has('query.execute.read') && !held.has('query.execute.write')) {
      return <NotPermitted />;
    }
    return (
      <Suspense
        fallback={
          <div className="flex min-h-0 flex-1 items-center justify-center">
            <Spinner label="Loading the console" />
          </div>
        }
      >
        {/*
          Passed in rather than read from the session inside the console,
          so the one place that decides what this user may reach is this
          function. Presentation only: the library's routes check the same
          permission server-side.
        */}
        <SqlConsole
          canManageScripts={held.has('query.saved.manage')}
          storageScope={session.user.oid}
        />
      </Suspense>
    );
  }
  if (path.startsWith('/api-console')) {
    return held.has('api.request.send') || held.has('api.request.mutate') ? (
      <ApiConsole />
    ) : (
      <NotPermitted />
    );
  }

  /**
   * The audit surfaces. `audit.read` covers search, the entitlement
   * snapshot and on-demand chain verification; `audit.export` is separate
   * because an evidence pack is a copy of the ledger that leaves the
   * environment and lives for seven years.
   */
  if (path.startsWith('/audit')) {
    return held.has('audit.read') ? <AuditPage /> : <NotPermitted />;
  }
  if (path.startsWith('/access-review')) {
    return held.has('audit.read') ? <AccessReviewPage /> : <NotPermitted />;
  }
  if (path.startsWith('/evidence')) {
    return held.has('audit.export') ? <EvidencePage /> : <NotPermitted />;
  }

  /**
   * No permission guard on either of these, and both are deliberate.
   *
   * A job list is scoped by the server to what the caller may see, and the
   * correct answer for someone who has submitted nothing is an empty list,
   * not a refusal. The approval queue is the same: it already excludes the
   * viewer's own requests, so a user who cannot approve anything sees
   * nothing -- which is more interpretable than a 403 on a link they were
   * shown.
   */
  if (path.startsWith('/jobs')) {
    return <JobsPage canViewAll={held.has('job.view.all')} />;
  }
  if (path.startsWith('/approvals')) {
    return <ApprovalsPage />;
  }

  if (path.startsWith('/admin/targets')) {
    return held.has('target.manage') || held.has('secret.bind') ? (
      <TargetsPage />
    ) : (
      <NotPermitted />
    );
  }
  if (path.startsWith('/admin/settings')) {
    return held.has('approval.rule.manage') ? <ApprovalRulesPage /> : <NotPermitted />;
  }

  /**
   * Personal settings, deliberately not under `/admin` and deliberately
   * behind no permission. Everyone who can sign in can choose the zone their
   * own dates are displayed in; there is no entitlement that could sensibly
   * gate it, and nothing here is visible to anyone else.
   *
   * The approval-rule editor lives at `/admin/settings`, which is a
   * different path and a different audience -- one configures the tool, the
   * other configures one person's view of it.
   */
  if (path.startsWith('/settings')) {
    return <SettingsPage session={session} />;
  }

  return <Overview session={session} />;
}

function NotPermitted(): ReactElement {
  return (
    <EmptyState
      icon={LockClosedIcon}
      title="You do not have access to this page"
      description="If you need it, ask an administrator to assign you a role that includes it."
    />
  );
}
